import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, MonitorSmartphone, PenSquare, Megaphone, TrendingUp, Bot,
  Sparkles, Clock, BadgeIndianRupee, HeadphonesIcon,
} from "lucide-react";
import AnimatedRobot from "@/components/AnimatedRobot";

const FEATURE_ICONS = [
  { Icon: MonitorSmartphone, label: "AI Website Builder" },
  { Icon: PenSquare, label: "Branding & Design" },
  { Icon: Megaphone, label: "Social Media Mgmt" },
  { Icon: TrendingUp, label: "SEO & Analytics" },
  { Icon: Bot, label: "AI Content Automation" },
];

const TRUST_BAR = [
  { Icon: Sparkles, label: "AI Automation" },
  { Icon: Clock, label: "Save Time & Cost" },
  { Icon: BadgeIndianRupee, label: "Increase Sales" },
  { Icon: HeadphonesIcon, label: "24/7 Support" },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* SPLIT HERO: light left, dark purple right */}
      <div className="relative grid lg:grid-cols-2 min-h-[88vh]">
        {/* ============ LEFT (light) ============ */}
        <div className="relative bg-gradient-to-br from-[#F4F1FF] via-[#E9E0FF] to-[#D8C8FF] px-6 lg:px-16 py-14 lg:py-20 flex flex-col justify-center overflow-hidden">
          {/* Floating accent blobs */}
          <FloatingBlob className="-top-16 -left-16 h-72 w-72" color="rgba(139,92,246,0.25)" delay={0} />
          <FloatingBlob className="bottom-10 right-0 h-56 w-56" color="rgba(59,130,246,0.18)" delay={1.5} />

          <motion.div {...fadeUp}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-violet-500/40 bg-white/60 text-violet-700 text-[11px] uppercase tracking-[0.25em] font-bold">
              AI-Powered Digital Marketing Solutions
            </span>
          </motion.div>

          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-heading mt-8 font-black tracking-tight leading-[0.95] text-[32px] sm:text-5xl lg:text-[60px] xl:text-7xl"
            data-testid="hero-headline"
          >
            <span className="block whitespace-nowrap">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#1E0E4A] via-[#5b21b6] to-[#7c3aed]">
                MakeYour
              </span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#7c3aed] via-[#8b5cf6] to-[#3b82f6]">
                Brand
              </span>
              <span className="text-[#1E0E4A]">.IN</span>
            </span>
          </motion.h1>

          <motion.div
            {...fadeUp}
            transition={{ duration: 0.7, delay: 0.18 }}
            className="mt-3 text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-[#1E0E4A]"
          >
            A Unit of <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-500 to-blue-600">SURAJIT MAJHI HUF</span>
          </motion.div>

          <motion.p
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.28 }}
            className="mt-7 text-lg lg:text-xl text-[#1E0E4A]/75 font-medium max-w-md"
          >
            Build Your Brand. Grow Your Business. <span className="font-bold text-violet-700">Powered by AI.</span>
          </motion.p>

          {/* Decorative dot dividers */}
          <div className="mt-8 flex items-center gap-2">
            <span className="h-1 w-16 rounded-full bg-gradient-to-r from-violet-500 to-blue-500" />
            <span className="h-1 w-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500" />
            <span className="h-1.5 w-1.5 rounded-full bg-violet-500" />
          </div>

          {/* CTA buttons */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 flex flex-col sm:flex-row gap-3"
          >
            <Link
              to="/register"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-bold text-sm text-white bg-gradient-to-r from-violet-600 via-purple-600 to-blue-600 shadow-lg shadow-violet-500/40 hover:shadow-violet-500/60 hover:-translate-y-0.5 transition"
            >
              Start building for free
              <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
            <Link
              to="/how-it-works"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-bold text-sm text-violet-700 bg-white/80 border-2 border-violet-500/30 hover:bg-white transition"
            >
              See how it works
            </Link>
          </motion.div>

          {/* Feature icons row */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.08 } },
            }}
            className="mt-12 grid grid-cols-5 gap-3"
          >
            {FEATURE_ICONS.map(({ Icon, label }, idx) => (
              <motion.div
                key={idx}
                variants={{
                  hidden: { opacity: 0, y: 18 },
                  show: { opacity: 1, y: 0 },
                }}
                className="flex flex-col items-center text-center"
              >
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 3 + idx * 0.3, repeat: Infinity, ease: "easeInOut", delay: idx * 0.2 }}
                  className="h-14 w-14 rounded-2xl bg-white shadow-lg shadow-violet-500/15 grid place-items-center mb-2 border border-violet-200"
                >
                  <Icon className="h-7 w-7 text-violet-600" strokeWidth={2.2} />
                </motion.div>
                <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#1E0E4A]/80 leading-tight">
                  {label}
                </span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* ============ RIGHT (dark cosmic) ============ */}
        <div className="relative overflow-hidden bg-gradient-to-br from-[#0c0626] via-[#1a0a3e] to-[#4c1d95] px-6 lg:px-12 py-14 lg:py-20 flex flex-col justify-center">
          {/* Animated network lines bg */}
          <NetworkBackground />

          {/* Floating particles */}
          <FloatingParticles />

          <div className="relative z-10">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="text-white/70 text-sm uppercase tracking-[0.3em] mb-3"
            >
              Your Complete
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-none"
            >
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-violet-300">
                AI Marketing
              </span>
              <br />
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-fuchsia-200 to-cyan-200">
                Department
              </span>
            </motion.h2>
            <div className="mt-3 flex items-center gap-3">
              <span className="h-[2px] w-12 bg-cyan-400" />
              <span className="text-xs uppercase tracking-[0.3em] text-cyan-300 font-bold">In One Platform</span>
              <span className="h-[2px] w-12 bg-cyan-400" />
            </div>

            {/* Bullet features */}
            <motion.ul
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.1, delayChildren: 0.4 } } }}
              className="mt-8 inline-flex flex-col gap-3 glass-strong rounded-2xl p-5 border border-fuchsia-500/30"
            >
              {["AI Automation", "Smart Content", "Lead Generation", "Brand Growth", "Real Results"].map((it) => (
                <motion.li
                  key={it}
                  variants={{
                    hidden: { opacity: 0, x: -10 },
                    show: { opacity: 1, x: 0 },
                  }}
                  className="flex items-center gap-3 text-white/90 font-semibold"
                >
                  <span className="h-2 w-2 rounded-full bg-gradient-to-r from-fuchsia-400 to-cyan-400" />
                  <span className="text-sm tracking-wide uppercase">{it}</span>
                </motion.li>
              ))}
            </motion.ul>
          </div>

          {/* The robot */}
          <div className="relative z-10 mt-6 lg:absolute lg:right-2 lg:bottom-4 lg:top-10 lg:w-[55%] flex items-end">
            <AnimatedRobot />
          </div>
        </div>
      </div>

      {/* Bottom trust strip */}
      <div className="bg-gradient-to-r from-[#1a0a3e] via-[#2d1466] to-[#4c1d95] border-y border-fuchsia-500/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-2.5 text-center">
          <span className="text-[11px] sm:text-xs uppercase tracking-[0.3em] text-cyan-200 font-semibold" data-testid="hero-legal-tagline">
            MakeYourBrand.IN <span className="text-white/55">(A Unit of SURAJIT MAJHI HUF)</span>
          </span>
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#1a0a3e] via-[#2d1466] to-[#4c1d95] border-b border-fuchsia-500/20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-8">
          {TRUST_BAR.map(({ Icon, label }, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08 }}
              className="flex items-center gap-3 justify-center md:justify-start"
            >
              <motion.div
                animate={{ rotate: [0, 6, 0, -6, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: idx * 0.3 }}
                className="h-10 w-10 rounded-full bg-gradient-to-br from-fuchsia-500/30 to-cyan-500/30 grid place-items-center border border-fuchsia-400/40"
              >
                <Icon className="h-5 w-5 text-cyan-300" />
              </motion.div>
              <span className="text-white font-bold text-xs sm:text-sm uppercase tracking-[0.15em]">
                {label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Helpers ============ */
function FloatingBlob({ className = "", color, delay = 0 }) {
  return (
    <motion.div
      className={`absolute rounded-full blur-3xl pointer-events-none ${className}`}
      style={{ background: color }}
      animate={{ y: [0, -20, 0], x: [0, 12, 0] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut", delay }}
    />
  );
}

function FloatingParticles() {
  const PARTICLES = Array.from({ length: 24 });
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {PARTICLES.map((_, i) => {
        const left = (i * 37) % 100;
        const top = (i * 53) % 100;
        const dur = 5 + (i % 5);
        return (
          <motion.span
            key={i}
            className="absolute h-1 w-1 rounded-full bg-cyan-300/70"
            style={{ left: `${left}%`, top: `${top}%` }}
            animate={{ y: [0, -30, 0], opacity: [0.2, 1, 0.2] }}
            transition={{ duration: dur, repeat: Infinity, ease: "easeInOut", delay: i * 0.2 }}
          />
        );
      })}
    </div>
  );
}

function NetworkBackground() {
  return (
    <svg className="absolute inset-0 w-full h-full opacity-20 pointer-events-none" viewBox="0 0 600 800" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="netLine" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#22D3EE" />
          <stop offset="100%" stopColor="#D946EF" />
        </linearGradient>
      </defs>
      {[
        ["50,100", "200,180", "380,80", "520,200"],
        ["80,320", "240,400", "420,300", "560,420"],
        ["120,560", "280,640", "440,520", "580,620"],
      ].map((row, ri) => (
        <g key={ri}>
          {row.map((c, ci) => {
            const [x, y] = c.split(",").map(Number);
            return <circle key={ci} cx={x} cy={y} r="3" fill="url(#netLine)" />;
          })}
          {row.slice(0, -1).map((c, ci) => {
            const [x1, y1] = c.split(",").map(Number);
            const [x2, y2] = row[ci + 1].split(",").map(Number);
            return <line key={ci} x1={x1} y1={y1} x2={x2} y2={y2} stroke="url(#netLine)" strokeWidth="1" />;
          })}
        </g>
      ))}
    </svg>
  );
}
